import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Button from '@classify/react-core-components/Button';
import TabPanel from '@classify/react-core-components/TabPanel';
import { styled } from '@classify/react-core-components/styles';
import { translate } from '@openclassrooms/translations';
import Content from './Content/Content';
import Menu from './Menu';
import type { Policy } from '../../../types';
const Root = styled('div')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  paddingTop: spacing(4),
  [breakpoints.up('sm')]: {
    margin: 'auto',
    maxWidth: 560
  },
  [breakpoints.up('md')]: {
    display: 'flex',
    marginTop: [spacing(4), '!important'],
    maxWidth: '100%'
  },
  [breakpoints.up('lg')]: {
    maxWidth: '83.05085%',
    width: 968,
    margin: 'auto'
  }
}));
const MenuContainer = styled('div')(({
  theme: {
    breakpoints,
    spacing
  }
}) => ({
  marginBottom: spacing(4),
  [breakpoints.up('md')]: {
    width: 232,
    marginRight: spacing(4)
  },
  [breakpoints.up('lg')]: {
    width: 268
  }
}));
const ContentContainer = styled('div')(({
  theme: {
    breakpoints,
    spacing,
    typography,
    palette
  }
}) => ({
  [breakpoints.up('md')]: {
    width: 496
  },
  [breakpoints.up('lg')]: {
    width: 568
  },
  '& p + p': {
    marginTop: spacing(2)
  },
  '& strong': {
    color: 'inherit',
    fontWeight: typography.fontWeightMedium
  },
  '& li': {
    marginBottom: spacing(1)
  },
  '& table, th, td': {
    border: '1px solid black',
    borderCollapse: 'collapse'
  },
  '& table': {
    margin: spacing(2, 0, 4, 0)
  },
  '& th': {
    backgroundColor: palette.layout.background.alternative
  },
  '& td, th': {
    fontWeight: 'normal',
    padding: spacing(1)
  },
  '& td': {
    verticalAlign: 'top',
    textAlign: 'left'
  },
  '& hr': {
    marginTop: spacing(5)
  }
}));
const ButtonContainer = styled('div')({
  zIndex: 1,
  background: '#fff',
  bottom: 0,
  padding: '0.5rem 1rem',
  opacity: 0,
  position: 'sticky',
  textAlign: 'center',
  transition: 'opacity 0.4s ease',
  '& svg': {
    boxShadow: 'none'
  }
});
export const useInView = options => {
  const $ = _c(5);
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);
  let t0;
  let t1;
  if ($[0] !== options) {
    t0 = () => {
      const currentRef = ref.current;
      if (!currentRef) {
        return;
      }
      const observer = new IntersectionObserver(t2 => {
        const [entry] = t2;
        setIsInView(entry.isIntersecting);
      }, {
        threshold: 0,
        ...options
      });
      observer.observe(currentRef);
      return () => {
        observer.unobserve(currentRef);
      };
    };
    t1 = [options];
    $[0] = options;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== isInView) {
    t2 = [ref, isInView];
    $[3] = isInView;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2 as const;
};
type PolicyContentProps = {
  index: string;
  policy: Policy;
};
export default function PolicyContent(t0) {
  const $ = _c(26);
  const {
    policy,
    index
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      threshold: 0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [targetRef, isInView] = useInView(t1);
  let t2;
  if ($[1] !== policy.sections) {
    t2 = policy.sections ? policy.sections : [];
    $[1] = policy.sections;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sections = t2;
  let t3;
  if ($[3] !== sections) {
    t3 = sections.map(_temp);
    $[3] = sections;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const menuItems = t3;
  let t4;
  if ($[5] !== menuItems) {
    t4 = <Menu items={menuItems} />;
    $[5] = menuItems;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4 || $[8] !== targetRef) {
    t5 = <MenuContainer><div ref={targetRef}>{t4}</div></MenuContainer>;
    $[7] = t4;
    $[8] = targetRef;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== policy) {
    t6 = <ContentContainer><Content policy={policy} /></ContentContainer>;
    $[10] = policy;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <Root>{t5}{t6}</Root>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== index || $[16] !== t7) {
    t8 = <TabPanel value={index}>{t7}</TabPanel>;
    $[15] = index;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = isInView ? 0 : 1;
  let t10;
  if ($[18] !== t9) {
    t10 = {
      opacity: t9
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Button variant="tertiary" onClick={_temp2} endIcon={<ArrowUpward style={{
      fontSize: 16
    }} />}>{translate("scrollToTop.actions.button_label")}</Button>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10) {
    t12 = <ButtonContainer style={t10}>{t11}</ButtonContainer>;
    $[21] = t10;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t8) {
    t13 = <>{t8}{t12}</>;
    $[23] = t12;
    $[24] = t8;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
}
function _temp2() {
  window.scrollTo({
    top: 0,
    behavior: "instant"
  });
}
function _temp(t0) {
  const {
    id,
    anchor,
    content,
    shortDescription,
    title,
    titleLevel
  } = t0;
  return {
    id,
    content: content ?? "",
    shortDescription,
    title,
    anchor,
    titleLevel,
    depth: titleLevel ?? 0,
    type: "content" as const
  };
}